import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../api/api';
import { normalizeUser, isAuthenticatedUser } from '../utils/authUser';
import { clearCachedNotifications } from '../hooks/useNotifications';

export const AuthContext = createContext();

function readInitialUser() {
  try {
    const saved = localStorage.getItem("user");
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (!isAuthenticatedUser(parsed)) {
      localStorage.removeItem("user");
      return null;
    }
    return normalizeUser(parsed);
  } catch {
    localStorage.removeItem("user");
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const initialUser = readInitialUser();

  const [user, setUserState] = useState(initialUser);
  const setUser = useCallback((u) => {
    if (u == null) {
      setUserState(null);
      return;
    }
    if (typeof u === "function") {
      setUserState((prev) => {
        const next = u(prev);
        if (next == null) return null;
        const normalized = normalizeUser(next);
        if (!isAuthenticatedUser(normalized)) {
          localStorage.removeItem("user");
          return null;
        }
        return normalized;
      });
      return;
    }
    const normalized = normalizeUser(u);
    if (!isAuthenticatedUser(normalized)) {
      setUserState(null);
      localStorage.removeItem("user");
      return;
    }
    setUserState(normalized);
  }, []);

  // No valid cached user → show loading until first checkAuth finishes
  const [loading, setLoading] = useState(initialUser == null);

  const checkAuthInProgress = React.useRef(false);

  const checkAuth = useCallback(async () => {
    if (checkAuthInProgress.current) {
      console.log("checkAuth already in progress, skipping duplicate call");
      return;
    }

    checkAuthInProgress.current = true;
    try {
      const response = await api.get("/user/me");
      const userData = response.data;

      if (!isAuthenticatedUser(userData)) {
        localStorage.removeItem("user");
        setUser(null);
        return;
      }

      // Covers the case where the previous session ended by closing the tab
      // instead of logging out: if the account now confirmed by the server
      // differs from whatever was last cached here, drop leftover local
      // notifications so they aren't shown as if they belonged to this account.
      try {
        const previousRaw = localStorage.getItem("user");
        const previousUser = previousRaw ? JSON.parse(previousRaw) : null;
        const previousIdentity = previousUser?.clientid ?? previousUser?.id ?? previousUser?.email ?? null;
        const nextIdentity = userData.clientid ?? userData.id ?? userData.email ?? null;
        if (previousIdentity != null && nextIdentity != null && previousIdentity !== nextIdentity) {
          clearCachedNotifications();
        }
      } catch {
        // ignore malformed cached user
      }

      if (!userData.profilePicture) {
        try {
          const picResponse = await api.get("/user/profile-picture");
          if (picResponse.data && picResponse.data.image) {
            userData.profilePicture = picResponse.data.image;
          }
        } catch (picErr) {
          console.log("No profile picture found");
        }
      }

      setUser(userData);
      localStorage.setItem("user", JSON.stringify(normalizeUser(userData)));
      // Lets providers above this one in the tree (e.g. ThemeContext, which
      // can't read AuthContext directly) react once a session is confirmed,
      // instead of only ever checking localStorage['user'] at their own mount.
      window.dispatchEvent(new Event("auth:confirmed"));
    } catch (err) {
      console.error("checkAuth / user/me failed:", err);
      // Never restore a ghost from localStorage when the server did not confirm the session
      localStorage.removeItem("user");
      setUser(null);
    } finally {
      checkAuthInProgress.current = false;
      setLoading(false);
    }
  }, [setUser]);

  // api.js dispatches this on any 401 from a protected page (expired session
  // or a stale cached user): drop the user so the route guard soft-redirects
  // to /login instead of the old window.location hard reload.
  useEffect(() => {
    const onUnauthorized = () => {
      localStorage.removeItem("user");
      clearCachedNotifications();
      setUser(null);
    };
    window.addEventListener("auth:unauthorized", onUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", onUnauthorized);
  }, [setUser]);

  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (isMounted) {
          await checkAuth();
        }
      } catch (error) {
        console.error("Initialization error", error);
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, [checkAuth]);

  // Admin page/action permissions from /permissions/me; null until loaded
  const [permissions, setPermissions] = useState(null);

  const refreshPermissions = useCallback(async () => {
    try {
      const res = await api.get('/permissions/me');
      setPermissions({ all: !!res.data?.all, keys: new Set((res.data?.permissions || []).map(k => k.toLowerCase())) });
    } catch {
      setPermissions({ all: false, keys: new Set() });
    }
  }, []);

  useEffect(() => {
    if (user?.isAdmin) refreshPermissions();
    else setPermissions(null);
  }, [user?.isAdmin, user?.id, user?.adminId, refreshPermissions]);

  const can = useCallback((category, name, action = 'view') => {
    if (!permissions) return false;
    return permissions.all || permissions.keys.has(`${category}/${name}/${action}`.toLowerCase());
  }, [permissions]);

  const logout = async (redirectTo) => {
    setUserState(null);
    localStorage.removeItem("user");
    clearCachedNotifications();
    document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", JSON.stringify(false));
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout error", err);
    } finally {
      // Callers may pass a landing path (e.g. "/" for the homepage escape below).
      // Any non-string arg (like a click event) falls back to the login page, so
      // existing callers (`onClick={logout}`) keep their old behavior.
      window.location.replace(typeof redirectTo === "string" ? redirectTo : "/login");
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, permissions, can, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  );
};
